import { useMemo, useState } from "react";
import { styled } from "@pigment-css/react";
import type { Dayjs } from "dayjs";
import { CalendarDays, CalendarRepeat, ChevronRight, ChevronRightThin } from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { MeetingOrderBy, MeetingScope } from "@acto/gql/graphql";
import { UNLIMITED_MEETINGS, useMeetings } from "@acto/taskpane/core/data-access/meetings/useMeetings";
import { useRecurringMeetings } from "@acto/taskpane/core/data-access/meetings/useRecurringMeetings";
import { useViewer } from "@acto/taskpane/core/contexts/ViewerContext";
import { todayUtc, toIsoDate } from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { RowBody, RowChevron, RowMeta, RowTitle } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Collapse } from "@acto/taskpane/field-sales-2/components/primitives/Collapse";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import {
  EditMeetingSheet,
  type EditableMeeting,
} from "@acto/taskpane/field-sales-2/components/patterns/EditMeetingSheet";
import { MeetingDeletedBanner } from "@acto/taskpane/field-sales-2/components/patterns/MeetingDeletedBanner";
import { useContributeChatScope } from "@acto/taskpane/core/chat-envelope";
import type { GlobalTokens } from "@acto/web-common/theme";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { formatDayMonthYear } from "@acto/taskpane/field-sales-2/utils/format";

const Banner = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
  boxSizing: "border-box",
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[20]}`,
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  // 28px per Figma; fieldSalesTwo.radius has no 28 key, so this stays a rem literal.
  borderRadius: "1.75rem",
}));

// One Banner slot for the first row + the expandable rest, so the closed (zero-height) Collapse
// adds no gap between the first row and the divider.
const Rows = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

// Owner-only editing means the same row renders as a button or as inert markup, so the two share
// their layout rather than restating it.
const rowLayout = (theme: GlobalTokens) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[10],
  width: "100%",
  textAlign: "left" as const,
  fontFamily: theme.fieldSalesTwo.font.family,
});

const Row = styled("button")(({ theme }) => ({
  ...rowLayout(theme),
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  ...pressInteraction(theme, { scale: 1 }),
}));

const StaticRow = styled("div")(({ theme }) => rowLayout(theme));

const IconBox = styled("span")(({ theme }) => ({
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  color: theme.fieldSalesTwo.colors.charts.primary,
}));

const Title = styled(RowTitle)(({ theme }) => ({
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Subtitle = styled(RowMeta)(({ theme }) => ({
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const ShowMoreButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[10],
  width: "100%",
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
  WebkitTapHighlightColor: "transparent",
  ...pressInteraction(theme, { scale: 1 }),
}));

// Mirror IconBox's 32px width so the chevron's center aligns with the row icon column.
const ShowMoreIconBox = styled("span")({
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
});

const ShowMoreChevron = styled(ChevronRight)<{ open?: boolean }>(({ theme }) => ({
  flexShrink: 0,
  width: theme.fieldSalesTwo.spacing[14],
  height: theme.fieldSalesTwo.spacing[14],
  color: theme.fieldSalesTwo.colors.neutral[800],
  transform: "rotate(90deg)", // ChevronRight → points down (collapsed)
  transition: `transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  variants: [{ props: { open: true }, style: { transform: "rotate(-90deg)" } }], // points up (expanded)
}));

const MoreRows = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  paddingTop: theme.fieldSalesTwo.spacing[16],
}));

interface UpcomingRow {
  key: string;
  date: Dayjs;
  startTime?: string | null;
  endTime?: string | null;
  isRecurring: boolean;
  /** Whether the row is the viewer's to edit — see `useViewer`'s `isViewer`. */
  isOwn: boolean;
  /** Set only for a colleague's meeting, and only when that colleague has an employee record. */
  ownerName: string | null;
  editTarget: EditableMeeting;
}

// The design drops the leading zero: 09:30 → 9:30.
const trimTime = (time: string) => time.replace(/^0/, "");

interface UpcomingMeetingsProps {
  clientId: string;
  street?: string;
  city?: string;
}

export function UpcomingMeetings({ clientId, street, city }: UpcomingMeetingsProps) {
  const t = useTranslations("fieldSalesTwo.upcomingMeetings");
  const { locale } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const [editTarget, setEditTarget] = useState<EditableMeeting | null>(null);
  const [deletedDate, setDeletedDate] = useState<string | null>(null);

  const today = toIsoDate(todayUtc());
  const { isViewer, colleagueName } = useViewer();
  const series = useRecurringMeetings({ clientId, scope: MeetingScope.ClientAccess });
  const standalone = useMeetings({
    clientId,
    standaloneOnly: true,
    fromDate: today,
    orderBy: MeetingOrderBy.ScheduledDateAsc,
    limit: UNLIMITED_MEETINGS,
    scope: MeetingScope.ClientAccess,
  });

  const rows = useMemo<UpcomingRow[]>(() => {
    const seriesRows = (series.data ?? []).flatMap((entry) =>
      entry.nextOccurrence?.occurrenceDate
        ? [
            {
              key: entry.id,
              date: entry.nextOccurrence.scheduledDate,
              startTime: entry.nextOccurrence.scheduledStartTime,
              endTime: entry.nextOccurrence.scheduledEndTime,
              isRecurring: true,
              isOwn: isViewer(entry.nextOccurrence.owner),
              ownerName: colleagueName(entry.nextOccurrence.owner),
              editTarget: {
                kind: "series" as const,
                seriesId: entry.id,
                occurrenceDate: entry.nextOccurrence.occurrenceDate.format("YYYY-MM-DD"),
                cadence: entry.cadence,
                date: entry.nextOccurrence.scheduledDate.format("YYYY-MM-DD"),
                startTime: entry.nextOccurrence.scheduledStartTime,
                endTime: entry.nextOccurrence.scheduledEndTime,
              },
            },
          ]
        : [],
    );
    const standaloneRows = (standalone.data ?? []).map((meeting) => ({
      key: meeting.id,
      date: meeting.scheduledDate,
      startTime: meeting.scheduledStartTime,
      endTime: meeting.scheduledEndTime,
      isRecurring: false,
      isOwn: isViewer(meeting.owner),
      ownerName: colleagueName(meeting.owner),
      editTarget: {
        kind: "standalone" as const,
        meetingId: meeting.id,
        date: meeting.scheduledDate.format("YYYY-MM-DD"),
        startTime: meeting.scheduledStartTime,
        endTime: meeting.scheduledEndTime,
      },
    }));

    return [...seriesRows, ...standaloneRows].sort(
      (a, b) => a.date.valueOf() - b.date.valueOf() || (a.startTime ?? "99:99").localeCompare(b.startTime ?? "99:99"),
    );
  }, [series.data, standalone.data, isViewer, colleagueName]);

  const address = [street, city].filter(Boolean).join(" · ");

  const meetingsFragment = {
    upcoming_meetings: rows
      .filter((row) => row.isOwn)
      .map((row) => ({
        date: row.date.format("YYYY-MM-DD"),
        start_time: row.startTime ?? undefined,
        end_time: row.endTime ?? undefined,
        address: address || undefined,
        recurring: row.isRecurring || undefined,
      })),
  };
  useContributeChatScope("customer-detail", meetingsFragment);
  const rowTitle = (row: UpcomingRow) => {
    const date = formatDayMonthYear(row.date.format("YYYY-MM-DD"), locale);
    if (!row.startTime || !row.endTime) return date;
    return `${date}, ${t("timeRange", { from: trimTime(row.startTime), to: trimTime(row.endTime) })}`;
  };

  const rowSubtitle = (row: UpcomingRow) => [row.ownerName, address].filter(Boolean).join(" · ");

  const renderRow = (row: UpcomingRow) => {
    const subtitle = rowSubtitle(row);
    const body = (
      <>
        <IconBox>
          {row.isRecurring ? <CalendarRepeat aria-label={t("recurring")} role="img" /> : <CalendarDays aria-hidden />}
        </IconBox>
        <RowBody>
          <Title>{rowTitle(row)}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </RowBody>
      </>
    );

    // The widget restricts editing to the owner, even where the backend would allow more.
    if (!row.isOwn) {
      return <StaticRow key={row.key}>{body}</StaticRow>;
    }

    return (
      <Row key={row.key} type="button" onClick={() => setEditTarget(row.editTarget)}>
        {body}
        <RowChevron aria-hidden>
          <ChevronRightThin strokeWidth={1.5} />
        </RowChevron>
      </Row>
    );
  };

  return (
    <>
      {rows.length > 0 && (
        <Banner aria-label={t("title")}>
          <Rows>
            {renderRow(rows[0])}
            {rows.length > 1 && (
              <Collapse open={expanded} id="upcoming-meetings-more">
                <MoreRows>{rows.slice(1).map(renderRow)}</MoreRows>
              </Collapse>
            )}
          </Rows>
          {rows.length > 1 && (
            <>
              <Divider />
              <ShowMoreButton
                type="button"
                aria-expanded={expanded}
                aria-controls="upcoming-meetings-more"
                onClick={() => setExpanded((current) => !current)}
              >
                <ShowMoreIconBox>
                  <ShowMoreChevron open={expanded} aria-hidden />
                </ShowMoreIconBox>
                {expanded ? t("showLess") : t("showMore")}
              </ShowMoreButton>
            </>
          )}
        </Banner>
      )}
      {editTarget && (
        <EditMeetingSheet
          isOpen
          onOpenChange={(open) => {
            if (!open) setEditTarget(null);
          }}
          meeting={editTarget}
          onDeleted={setDeletedDate}
        />
      )}
      <MeetingDeletedBanner open={deletedDate != null} date={deletedDate ?? ""} onClose={() => setDeletedDate(null)} />
    </>
  );
}
